import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface ContactIntent {
  productId: string;
  userId?: string;
  selectedIntent: 'quote' | 'inquiry';
  suggestedIntent: 'quote' | 'inquiry';
  userContext: {
    sessionDuration: number;
    hasViewedProduct: boolean;
    userType: 'new' | 'returning' | 'business';
    productPrice?: number;
    timeOnPage?: number;
  };
  timestamp: string;
  followupAction?: 'completed' | 'abandoned' | 'switched';
}

export function useContactAnalytics() {
  const [intentHistory, setIntentHistory] = useState<ContactIntent[]>([]);

  // Track user intent selection
  const trackContactIntent = async (intent: ContactIntent) => {
    try {
      // Store in database for analysis
      const { error } = await supabase
        .from('contact_analytics')
        .insert({
          product_id: intent.productId,
          user_id: intent.userId,
          selected_intent: intent.selectedIntent,
          suggested_intent: intent.suggestedIntent,
          user_context: intent.userContext,
          created_at: intent.timestamp
        });

      if (error) {
        console.error('Error tracking contact intent:', error);
      }

      // Store locally for immediate analysis
      setIntentHistory(prev => [...prev, intent]);
    } catch (error) {
      console.error('Error in trackContactIntent:', error);
    }
  };

  // Analyze intent accuracy
  const analyzeIntentAccuracy = () => {
    if (intentHistory.length === 0) return null;

    const correctPredictions = intentHistory.filter(
      intent => intent.selectedIntent === intent.suggestedIntent
    ).length;

    const accuracy = (correctPredictions / intentHistory.length) * 100;

    return {
      accuracy,
      totalSamples: intentHistory.length,
      correctPredictions,
      quoteRequests: intentHistory.filter(i => i.selectedIntent === 'quote').length,
      inquiries: intentHistory.filter(i => i.selectedIntent === 'inquiry').length
    };
  };

  // Get user behavior patterns
  const getUserBehaviorPatterns = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('contact_analytics')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      return {
        preferredIntent: getMostCommonIntent(data),
        averageSessionDuration: getAverageSessionDuration(data),
        conversionRate: getConversionRate(data)
      };
    } catch (error) {
      console.error('Error getting user behavior patterns:', error);
      return null;
    }
  };

  // Helper functions
  const getMostCommonIntent = (data: any[]) => {
    const intentCounts = data.reduce((acc, item) => {
      acc[item.selected_intent] = (acc[item.selected_intent] || 0) + 1;
      return acc;
    }, {});

    return Object.keys(intentCounts).reduce((a, b) => 
      intentCounts[a] > intentCounts[b] ? a : b
    );
  };

  const getAverageSessionDuration = (data: any[]) => {
    const durations = data.map(item => item.user_context?.sessionDuration || 0);
    return durations.reduce((sum, duration) => sum + duration, 0) / durations.length;
  };

  const getConversionRate = (data: any[]) => {
    const completed = data.filter(item => item.followup_action === 'completed').length;
    return data.length > 0 ? (completed / data.length) * 100 : 0;
  };

  // Smart intent suggestion based on context
  const suggestIntent = (context: ContactIntent['userContext']): 'quote' | 'inquiry' => {
    let score = 0;

    // Business users are more likely to want quotes
    if (context.userType === 'business') score += 30;
    if (context.userType === 'returning') score += 15;

    // Time spent indicates serious interest
    if (context.sessionDuration > 120) score += 25;
    if (context.timeOnPage && context.timeOnPage > 60) score += 20;

    // Product detail views suggest purchase intent
    if (context.hasViewedProduct) score += 25;

    // High-value products suggest serious buyers
    if (context.productPrice && context.productPrice > 100000) score += 20;
    if (context.productPrice && context.productPrice > 500000) score += 30;

    // Score > 50 suggests quote intent
    return score > 50 ? 'quote' : 'inquiry';
  };

  return {
    trackContactIntent,
    analyzeIntentAccuracy,
    getUserBehaviorPatterns,
    suggestIntent,
    intentHistory
  };
} 